import React from 'react';
import { Col, Typography } from 'antd';
import {
  BankOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  FileTextOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import type { FinanceDashboardActionSummary } from '@/services/financeDashboard.service';

const { Text } = Typography;

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const FinanceOutlookRibbon: React.FC<{
  summary: FinanceDashboardActionSummary;
  formatCurrency: (v: number) => string;
}> = ({ summary, formatCurrency }) => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const drafts =
    (summary.draft_receipt_vouchers_count ?? 0) +
    (summary.draft_payment_vouchers_count ?? 0) +
    (summary.draft_journal_vouchers_count ?? 0);
  const unposted = summary.month_end?.unposted_journals ?? 0;

  return (
    <div id="outlook">
      <ControlTowerMetricStrip zone="metricsOrders" title={t('controlTower.outlookTitle')}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.cashAndBank')}
            count={0}
            amount={summary.cash_and_bank_balance}
            formatCurrency={formatCurrency}
            valueDisplay={formatCurrency(summary.cash_and_bank_balance ?? 0)}
            icon={<BankOutlined />}
            urgency="neutral"
            footerLink={{
              label: t('controlTower.cashIntelligence'),
              onClick: () => navigate('/owner-intelligence/cash'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.arOverdue')}
            count={summary.overdue_customer_invoice_count}
            amount={summary.overdue_customer_invoice_value}
            formatCurrency={formatCurrency}
            icon={<WarningOutlined />}
            urgency={summary.overdue_customer_invoice_count > 0 ? 'critical' : 'neutral'}
            extra={
              (summary.overdue_customer_aging?.['90+']?.count ?? 0) > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('controlTower.aged90Plus', { count: summary.overdue_customer_aging['90+'].count })}
                </Text>
              ) : undefined
            }
            footerLink={{
              label: t('controlTower.followUp'),
              onClick: () => scrollTo('collections-payables'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.apDueIn7d')}
            count={summary.due_soon_supplier_invoice_count}
            amount={summary.due_soon_supplier_invoice_value}
            formatCurrency={formatCurrency}
            icon={<ClockCircleOutlined />}
            urgency={summary.due_soon_supplier_invoice_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: t('controlTower.queue'),
              onClick: () => scrollTo('collections-payables'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.arDueIn7d')}
            count={summary.due_soon_customer_invoice_count}
            amount={summary.due_soon_customer_invoice_value}
            formatCurrency={formatCurrency}
            icon={<ClockCircleOutlined />}
            urgency={summary.due_soon_customer_invoice_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: t('controlTower.queue'),
              onClick: () => scrollTo('collections-payables'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.openArApSources')}
            count={(summary.open_ar_sources_count ?? 0) + (summary.open_ap_sources_count ?? 0)}
            icon={<SwapOutlined />}
            urgency={
              (summary.open_ar_sources_count ?? 0) + (summary.open_ap_sources_count ?? 0) > 0
                ? 'attention'
                : 'neutral'
            }
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('controlTower.arApSourcesExtra', {
                  ar: summary.open_ar_sources_count,
                  ap: summary.open_ap_sources_count,
                })}
              </Text>
            }
            footerLink={{
              label: t('controlTower.settlement'),
              onClick: () => navigate('/fin/reports/settlement-register'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.paymentCommitments')}
            count={summary.payment_commitments_count ?? 0}
            icon={<FileTextOutlined />}
            urgency={(summary.payment_commitments_count ?? 0) > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: t('controlTower.advances'),
              onClick: () => navigate('/fin/reports/supplier-advance-register'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.draftsUnposted')}
            count={drafts + unposted}
            icon={<FileTextOutlined />}
            urgency={drafts + unposted > 0 ? 'attention' : 'neutral'}
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('controlTower.draftsUnpostedExtra', { drafts, unposted })}
              </Text>
            }
            footerLink={{
              label: t('controlTower.navMonthEnd'),
              onClick: () => scrollTo('month-end'),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title={t('controlTower.dnSiGrnBill')}
            count={
              (summary.sales_invoices_ready_count ?? 0) + (summary.supplier_invoices_ready_count ?? 0)
            }
            icon={<InboxOutlined />}
            urgency={
              (summary.sales_invoices_ready_count ?? 0) + (summary.supplier_invoices_ready_count ?? 0) >
              0
                ? 'attention'
                : 'neutral'
            }
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('controlTower.dnSiGrnBillExtra', {
                  tax: summary.sales_invoices_ready_count,
                  bills: summary.supplier_invoices_ready_count,
                })}
              </Text>
            }
            footerLink={{
              label: t('controlTower.docPrep'),
              onClick: () => scrollTo('document-preparation'),
            }}
          />
        </Col>
      </ControlTowerMetricStrip>
    </div>
  );
};

export default FinanceOutlookRibbon;
